import type { ReactElement } from "react";
import { useEffect, useState } from "react";

interface IProgressiveImageState {
  image: string;
  loading: boolean;
}

interface IProgressiveImageProps {
  src: string;
  placeholder: string;
  children: (state: IProgressiveImageState) => ReactElement;
}

export default function ProgressiveImage({ src, placeholder, children }: IProgressiveImageProps): ReactElement {
  const image = typeof window !== "undefined" ? new Image() : null;
  const [loading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!image) {
      return;
    }

    image.src = src;

    // we don't need to do anything because the image is loaded from cache
    if (image.complete) {
      return;
    }

    image.onload = () => setIsLoading(false);
    image.onerror = () => setIsLoading(false);

    if (!image.complete) {
      setIsLoading(true);
    }
  }, []);

  if (typeof window === "undefined") {
    return children({
      image: src,
      loading: false,
    });
  }

  return children({
    image: loading ? placeholder : src,
    loading,
  });
}
